import { FC, useEffect, useState } from 'react';
import { Button, Chip, DialogContent, Stack, TextField, Typography } from '@mui/material';
import dayjs from 'dayjs';

import remindersApi from '../../api/reminders/api';
import type { Reminder, ReminderInput } from '../../api/reminders/types';
import { displayToast } from '../RTToast';
import { DraggableDialog } from '../DraggablePaper/DraggablePaper';
import ModalFormTitle from '../Tables/TableWithModalEdit/modalFormPieces/ModalFormTitle';
import { DialogActionsWrapper } from '../Tables/TableWithModalEdit/modalFormPieces/ModalFormActions/ModalFormActions';

interface Props {
    open: boolean;
    onClose: () => void;
    /** Editing an existing reminder; omitted to create one. */
    reminder?: Reminder | null;
    /** For a new reminder created from a travel file. */
    travelFile?: { _id: number | string; refNumber: string | number } | null;
    onSaved?: (reminder: Reminder) => void;
}

/** The next whole hour from now — the default time of a new reminder. */
const nextHour = () => dayjs().add(1, 'hour').startOf('hour');

/**
 * Create / edit a personal reminder: text, date and time, and the travel file it is about when
 * it was created from one. The date and time are the user's local time; they are sent as an
 * instant (ISO with offset), so the reminder fires at that moment whatever the server's zone.
 */
const ReminderEditDialog: FC<Props> = ({ open, onClose, reminder, travelFile, onSaved }) => {
    const [text, setText] = useState('');
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [link, setLink] = useState<{ id: number | null; ref: string | null }>({ id: null, ref: null });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        const at = reminder ? dayjs(reminder.remindAt) : nextHour();
        setText(reminder?.text ?? '');
        setDate(at.format('YYYY-MM-DD'));
        setTime(at.format('HH:mm'));
        setLink(
            reminder
                ? { id: reminder.travelFileId, ref: reminder.travelFileRef }
                : travelFile
                ? { id: Number(travelFile._id), ref: String(travelFile.refNumber) }
                : { id: null, ref: null }
        );
    }, [open, reminder, travelFile]);

    const at = dayjs(`${date}T${time}`);
    const valid = text.trim().length > 0 && at.isValid();

    const save = async () => {
        if (!valid) return;
        const input: ReminderInput = {
            text: text.trim(),
            remindAt: at.toISOString(),
            travelFileId: link.id,
            travelFileRef: link.ref
        };
        setSaving(true);
        try {
            const saved = reminder ? await remindersApi.update(reminder._id, input) : await remindersApi.create(input);
            onSaved?.(saved);
            onClose();
        } catch (e: any) {
            displayToast('error', 'Reminder not saved', e?.response?.data?.message || 'Something went wrong');
        } finally {
            setSaving(false);
        }
    };

    return (
        <DraggableDialog
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: 360, maxWidth: 'calc(100% - 32px)' } }}
        >
            <ModalFormTitle title={reminder ? 'Edit Reminder' : 'New Reminder'} toggleDialog={onClose} />
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <TextField
                        label="Reminder"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        multiline
                        minRows={2}
                        maxRows={6}
                        autoFocus
                        fullWidth
                        inputProps={{ maxLength: 500, dir: 'auto' }}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Stack direction="row" spacing={1}>
                        <TextField
                            label="Date"
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Time"
                            type="time"
                            value={time}
                            onChange={(e) => setTime(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Stack>
                    {link.id ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                                Travel file
                            </Typography>
                            <Chip
                                size="small"
                                label={link.ref || link.id}
                                onDelete={() => setLink({ id: null, ref: null })}
                            />
                        </Stack>
                    ) : null}
                </Stack>
            </DialogContent>
            <DialogActionsWrapper>
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ width: '100%' }}>
                    <Button onClick={onClose} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={save} variant="contained" disabled={!valid || saving}>
                        Save
                    </Button>
                </Stack>
            </DialogActionsWrapper>
        </DraggableDialog>
    );
};

export default ReminderEditDialog;
